import React, { useEffect, useMemo, useRef } from "react";
import { sum } from "lodash";
import { useParams } from "react-router-dom";

import {
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  Output,
  Share,
} from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { UploadSkeleton } from "./UploadSkeleton";
import {
  ActionButtons,
  AudienceSegmentsSize,
  AudienceSize,
  FileUpload,
  HelpBox,
  HelpText,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, CustomerMode, GeoMode, SelectableMenuCard, TravelTimeMode } from "@/components/molecules";
import { useAudienceContext } from "@/context";
import { useActionWorkflow, useAudience, useAudienceUtils, useServices } from "@/hooks";
import {
  AudienceSummary,
} from "@/types";
import {
  createUploadDefinition,
  uploadModeOptions,
} from "@/utils";

const UploadPage: React.FC = () => {

  const { ui } = useServices();
  const { state, dispatch } = useAudienceContext();

  // Fetch Page Data
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "upload");
  const uploadFile = audience?.upload_file;
  const uploadMode = audience?.upload_mode;

  const { resetAction } = useActionWorkflow();
  const { utils, action } = useAudienceUtils(audienceId, audience);
  const isMounted = useRef(false);

  // Derive a single Page State
  const pageState = useMemo(() => {

    // Fetch status
    const isLoading = audLoad;
    const loadError = !!(audErr);

    // Upload status
    const hasMode = audience?.upload_mode !== undefined
    const hasFile = audience?.upload_file instanceof File
    const hasValidFile = state.validFile && state.validFile.format
    const canUpload = hasMode && hasFile && hasValidFile

    // Audience status
    const isNewAudience = audienceId === undefined;
    const isAudShared = !!audience?.shared;
    const isAudProcessing = audience?.status === 'processing';
    const isAudComplete = audience?.status === 'complete';
    const isAudError = audience?.status === 'error';

    // Build status
    const isBuilt = !!(audience?.audience_size && audience?.audience_size.length > 0);
    const justRebuilt = action.type === 'build' && action.status === 'success';
    const audDefinition = createUploadDefinition(audience);
    const hasChanged = audDefinition !== state.currentDefinition;

    // Insight status
    const insightIsStale = hasChanged || justRebuilt;

    // Action status
    const isActioning = action.status === 'loading';

    // Locked status
    const isLocked = isLoading || loadError || isAudProcessing || isAudShared || isActioning;

    return {
      isLoading,  // Data is loading
      isReady: !isLoading && !loadError, // Data is ready to go
      isLocked,  // Edit access is disabled
      isActioning, // Page is doing something
      isBuilt,  // The audience has been built
      hasChanged, // Has the audience definition changed?
      insightIsStale, // Does the insight need regenerating?
      hasFile, // Has the user uploaded a file

      errors: {
        // Page Errors
        AudienceNotFound: audErr,
        AudienceError: isAudError,

        // Action Errors
        BuildError: (action.type === 'build' && action.status === 'error' && !hasChanged) && action.error,
        InsightError: (action.type === 'insight' && action.status === 'error' && !hasChanged) && action.error,
        TraveltimeError: (action.type === 'traveltime' && action.status === 'error' && !hasChanged) && action.error,

        // Audience Status
        AudienceProcessing: isAudProcessing && !isActioning,
        AudienceShared: isAudShared,
      },

      can: {
        build: canUpload && hasChanged && !isLocked && !isActioning,
        insight: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning),
        export: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning) && isAudComplete,
        delete: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
        share: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
      }

    };

  }, [state, action, audience, audienceId, audLoad, audErr]);

  useEffect(() => {
    if (pageState.hasChanged) {
      resetAction();
    }
  }, [pageState.hasChanged, resetAction]);

  const handleChangeMode = (mode: string) => {
    dispatch({ type: "CHANGE_UPLOAD_MODE", payload: mode });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      dispatch({ type: "ADD_UPLOAD_FILE", payload: files[0] });
    }
  };

  useEffect(() => {
    // Check the file that has been uploaded
    uploadMode
    && uploadFile
    && uploadFile instanceof File
    && utils.handleCheckFile(uploadFile, uploadMode);
  }, [uploadFile]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return;
    }
    dispatch({ type: "CLEAR_UPLOAD_FILE" });
    dispatch({ type: "CHECK_UPLOAD_FILE", payload: null });
  }, [uploadMode])

  const summariseAudience = (summary: AudienceSummary[]) => {
    return {
      num_postcodes: sum(summary.map((x) => x.num_postcodes)),
      num_sectors: sum(summary.map((x) => x.num_sectors)),
      num_households: sum(summary.map((x) => x.num_households)),
      population_count: sum(summary.map((x) => x.population_count)),
    };
  };

  const pageActions = useMemo(() => {
    const isBuilt = pageState.isBuilt;
    const hasChanged = pageState.hasChanged;
    const isComplete = audience?.status === 'complete';
    const isLoading = (type: string) => action.status === 'loading' && action.type === type;

    return [
      {
        id: 'delete',
        label: 'Delete',
        icon: Delete,
        onClick: () =>
          ui.openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/audience",
            },
          }),
        disabled: !pageState.can.delete,
        isPrimary: false,
        color: 'error' as const
      },
      {
        id: 'share',
        label: 'Share',
        icon: Share,
        onClick: () =>
          ui.openDialog({
            type: "shareAudience",
            props: {
              selectedEntity: audience,
              onShare: async (ids: number[]) => {
                await utils.handleShare(ids);
              },
            },
          }),
        disabled: !pageState.can.share,
        isPrimary: false,
      },
      {
        id: 'build',
        label: isBuilt ? 'Rebuild' : 'Build',
        icon: Construction,
        onClick: utils.handleBuildUpload,
        loading: isLoading('build'),
        disabled: !pageState.can.build,
        isPrimary: (pageState.can.build || isLoading('build')) && (!isBuilt || hasChanged),
      },
      {
        id: 'insight',
        label: 'Insight',
        icon: BarChartOutlined,
        onClick: () => utils.handleInsight(pageState.insightIsStale),
        loading: isLoading('insight'),
        disabled: !pageState.can.insight,
        isPrimary: isBuilt && !hasChanged,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Output,
        onClick: utils.handleExport,
        disabled: !pageState.can.export,
        isPrimary: isBuilt && !hasChanged && isComplete,
      }
    ]
  }, [action, audience, pageState, ui, utils]);

  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={action.status === "loading"}
      loadingText={action.message}
    >
      {pageState.isLoading ? (
        <UploadSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                {/* TODO - Move to component */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !pageState.isLocked && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          ui.openDialog({
                            type: "editAudience",
                            props: {
                              audienceId: audienceId,
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceId: string,
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await utils.handleUpdate(
                                  audienceId,
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={summariseAudience(audience.audience_size)}
                  />
                )}
              </Grid>

              {/* Heading and Mode Select */}

              <Grid size={12}>
                <HelpText
                  text="Select Mode"
                  helpText="Define the type of upload audience you are creating"
                  variant="body1"
                />
              </Grid>
              <Grid container size={12} spacing={2}>
                {uploadModeOptions.map((option, index) => (
                  <Grid key={index} pb={2}>
                    <SelectableMenuCard
                      title={option.label}
                      text={option.description}
                      onClick={() => handleChangeMode(option.value)}
                      icon={option.icon}
                      disabled={option.disabled || pageState.isLocked}
                      flag={option.flag}
                      flagDesc={option.flagDescription}
                      selected={audience.upload_mode === option.value}
                      height="152px"
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Mode Options */}
              {audience.upload_mode && (
                <>
                  {audience.upload_mode === 'travel_time' ? (
                      <TravelTimeMode audience={audience} readOnly={pageState.isLocked} />
                    )
                  : audience.upload_mode === 'customer' ? (
                      <CustomerMode readOnly={pageState.isLocked} />
                      ) : (
                      <GeoMode readOnly={pageState.isLocked} />
                    )
                  }
                  {pageState.hasChanged && !pageState.hasFile && uploadFile && (
                    <Typography variant="caption" color="error">
                      Please re-upload your file if you would like to re-build your Audience,
                      the filename is listed below for convenience.
                    </Typography>
                  )}
                  <Grid size={12}>
                    <FileUpload
                      currentFile={audience.upload_file}
                      validFile={state.validFile}
                      loading={pageState.isLoading}
                      onChange={handleFileUpload}
                      allowSegments={true}
                      disabled={pageState.isLocked}
                    />
                  </Grid>
                </>
              )}

              {/* Audience Size */}
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="The number of postcodes in your upload, and how many households/people this represents"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size.length > 1 ? (
                    <AudienceSegmentsSize data={audience.audience_size} />
                  ) : (
                    <AudienceSize data={audience.audience_size[0]} />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default UploadPage;
